import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../environments/environment';

/**
 * Página de acesso negado
 * 
 * Exibida quando usuário tenta acessar recurso sem permissão adequada
 */
@Component({
  selector: 'app-access-denied',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  template: `
    <div class="flex items-center justify-center min-h-screen bg-pmdf-bg-subtle px-4">
      <div class="max-w-md w-full text-center">
        <div class="mb-6">
          <svg class="w-20 h-20 mx-auto text-pmdf-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        
        <h1 class="text-3xl font-bold text-pmdf-text-primary mb-3">Acesso Negado</h1>
        <p class="text-pmdf-muted-70 mb-6">
          Você não tem permissão para acessar este recurso.
        </p>
        
        @if (showDebugInfo()) {
          <div class="bg-pmdf-warning-soft border border-pmdf-warning rounded-pmdf-card p-4 mb-6 text-left">
            <h3 class="text-sm font-semibold text-pmdf-warning mb-2">Informações de Debug:</h3>
            <div class="text-xs text-pmdf-warning space-y-1">
              @if (requiredRoles()) {
                <p><strong>Roles necessárias:</strong> {{ requiredRoles() }}</p>
              }
              @if (userRoles()) {
                <p><strong>Suas roles:</strong> {{ userRoles() }}</p>
              }
              @if (returnUrl()) {
                <p><strong>URL solicitada:</strong> {{ returnUrl() }}</p>
              }
            </div>
          </div>
        }
        
        <div class="space-y-3">
          <a 
            routerLink="/inicio" 
            class="block w-full px-6 py-3 bg-pmdf-primary text-pmdf-surface rounded-pmdf-card hover:bg-pmdf-primary/90 focus:outline-none focus:ring-2 focus:ring-pmdf-primary focus:ring-offset-2 transition"
          >
            Voltar ao Início
          </a>
          
          <button 
            (click)="logout()"
            class="block w-full px-6 py-3 bg-pmdf-bg-muted text-pmdf-muted-70 rounded-pmdf-card hover:bg-pmdf-bg-muted/80 focus:outline-none focus:ring-2 focus:ring-pmdf-border focus:ring-offset-2 transition"
          >
            Fazer Logout
          </button>
        </div>
        
        <p class="text-sm text-pmdf-muted-55 mt-6">
          Se você acredita que deveria ter acesso a este recurso, entre em contato com o administrador do sistema.
        </p>
      </div>
    </div>
  `,
  styles: []
})
export class AccessDeniedPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  
  protected readonly showDebugInfo = signal(!environment.production);
  protected readonly requiredRoles = signal<string | null>(null);
  protected readonly returnUrl = signal<string | null>(null);
  protected readonly userRoles = signal<string | null>(null);
  
  constructor() {
    // Extrai parâmetros da query string
    this.route.queryParams.subscribe(params => {
      this.returnUrl.set(params['returnUrl'] || null);
      this.requiredRoles.set(params['requiredRoles'] || null);
      
      const user = this.authService.user();
      if (user) {
        const allRoles = [
          ...user.roles.realm,
          ...Object.values(user.roles.resource).flat()
        ];
        this.userRoles.set(allRoles.join(', '));
      }
    });
  }
  
  protected logout(): void {
    this.authService.logout();
  }
}

